import React, { useState, useEffect, useRef } from "react";
import {
    collection, query, where, onSnapshot,
    doc, updateDoc, setDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { placeOrderWithToken } from "../firebase/orderHelper";
import { MENU, CATEGORY_ICONS } from "../data/menu";
import { TRANSLATIONS } from "../data/translations";

const SHOP_ID = process.env.REACT_APP_SHOP_ID;
const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS;
const SHOP_NAME = process.env.REACT_APP_SHOP_NAME;

const playNotificationSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const playBeep = (freq, startTime, duration) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = "sine";
            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.4, startTime + 0.01);
            gain.gain.linearRampToValueAtTime(0, startTime + duration);
            osc.start(startTime);
            osc.stop(startTime + duration);
        };
        playBeep(523, ctx.currentTime, 0.15);
        playBeep(659, ctx.currentTime + 0.18, 0.15);
        playBeep(784, ctx.currentTime + 0.36, 0.25);
    } catch (e) { }
};

const getTodayIST = () =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const formatTime = (seconds) => {
    if (!seconds) return "";
    return new Date(seconds * 1000).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true
    });
};

const formatDateTime = (seconds) => {
    if (!seconds) return "";
    return new Date(seconds * 1000).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: true
    });
};

export default function AdminApp() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [pass, setPass] = useState("");
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [view, setView] = useState("orders");
    const [newCart, setNewCart] = useState({});
    const [newCustomerName, setNewCustomerName] = useState("");
    const [newCategory, setNewCategory] = useState(Object.keys(MENU)[0]);
    const [placing, setPlacing] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [dayStartTime, setDayStartTime] = useState(null);
    const [showAllItems, setShowAllItems] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [dashSessionIdx, setDashSessionIdx] = useState(0);
    const [dashView, setDashView] = useState(null);
    const [stockStatus, setStockStatus] = useState({});
    const [popularItemIds, setPopularItemIds] = useState(new Set());
    const prevOrderCountRef = useRef(0);
    const isFirstLoadRef = useRef(true);

    // ── Settings state ──
    const [language, setLanguage] = useState("en"); // updated from Firestore
    const [stockToggleEnabled, setStockToggleEnabled] = useState(
        () => localStorage.getItem("stockToggleEnabled") !== "false"
    );

    // ── Translation helper ──
    const t = (key) => TRANSLATIONS[language]?.[key] ?? TRANSLATIONS.en[key] ?? key;

    // ── Status config (translated) ──
    const getStatusConfig = (lang) => ({
        pending: { label: TRANSLATIONS[lang]?.status_new || "New", color: "#ff6b35", next: "approved", nextLabel: TRANSLATIONS[lang]?.action_approve || "✅ Approve", bgLight: "#fff3ee" },
        approved: { label: TRANSLATIONS[lang]?.status_approved || "Approved", color: "#2196f3", next: "paid", nextLabel: TRANSLATIONS[lang]?.action_paid || "💰 Paid", bgLight: "#e3f2fd" },
        paid: { label: TRANSLATIONS[lang]?.status_paid || "Paid", color: "#9c27b0", next: "preparing", nextLabel: TRANSLATIONS[lang]?.action_prepare || "🍧 Prepare", bgLight: "#f3e5f5" },
        preparing: { label: TRANSLATIONS[lang]?.status_preparing || "Preparing", color: "#ff9800", next: "ready", nextLabel: TRANSLATIONS[lang]?.action_ready || "🔔 Ready", bgLight: "#fff8e1" },
        ready: { label: TRANSLATIONS[lang]?.status_ready || "Ready", color: "#4caf50", next: "complete", nextLabel: TRANSLATIONS[lang]?.action_complete || "✔ Complete", bgLight: "#e8f5e9" },
        complete: { label: TRANSLATIONS[lang]?.status_complete || "Complete", color: "#9e9e9e", next: null, nextLabel: null, bgLight: "#f5f5f5" },
        cancelled: { label: TRANSLATIONS[lang]?.status_cancelled || "Cancelled", color: "#f44336", next: null, nextLabel: null, bgLight: "#ffebee" },
    });

    const STATUS_CONFIG = getStatusConfig(language);

    // ── Save language to Firestore ──
    const handleSetLanguage = async (lang) => {
        setLanguage(lang);
        await setDoc(doc(db, "shopSettings", SHOP_ID), { language: lang }, { merge: true });
    };

    // ── Persist stock toggle ──
    const handleSetStockToggle = (val) => {
        setStockToggleEnabled(val);
        localStorage.setItem("stockToggleEnabled", val ? "true" : "false");
    };

    // ── Load shopSettings (day start time + language) ──
    useEffect(() => {
        if (!loggedIn) return;
        const ref = doc(db, "shopSettings", SHOP_ID);
        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) {
                setDayStartTime(snap.data().dayStartTime?.seconds || null);
                if (snap.data().language) setLanguage(snap.data().language);
            }
        });
        return unsub;
    }, [loggedIn]);

    // ── Load sessions ──
    useEffect(() => {
        if (!loggedIn) return;
        const q = query(collection(db, "shopSessions"), where("shopId", "==", SHOP_ID));
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0));
            setSessions(data);
        });
        return unsub;
    }, [loggedIn]);

    // ── Load stock status ──
    useEffect(() => {
        if (!loggedIn) return;
        const ref = doc(db, "stockStatus", SHOP_ID);
        const unsub = onSnapshot(ref, (snap) => {
            setStockStatus(snap.exists() ? snap.data() : {});
        });
        return unsub;
    }, [loggedIn]);

    // ── Popular items — top 3 per category ──
    useEffect(() => {
        if (!loggedIn) return;
        const q = query(collection(db, "orders"), where("shopId", "==", SHOP_ID));
        const unsub = onSnapshot(q, (snap) => {
            const itemCounts = {};
            const itemCategory = {};
            snap.docs.forEach(d => {
                d.data().items?.forEach(item => {
                    if (item.id) {
                        itemCounts[item.id] = (itemCounts[item.id] || 0) + item.qty;
                        if (item.category) itemCategory[item.id] = item.category;
                    }
                });
            });
            const byCategory = {};
            Object.entries(itemCounts).forEach(([id, qty]) => {
                const cat = itemCategory[id] || "Other";
                if (!byCategory[cat]) byCategory[cat] = [];
                byCategory[cat].push([id, qty]);
            });
            const popularIds = [];
            Object.values(byCategory).forEach(entries => {
                entries.sort((a, b) => b[1] - a[1]).slice(0, 3).forEach(([id]) => popularIds.push(id));
            });
            setPopularItemIds(new Set(popularIds));
        });
        return unsub;
    }, [loggedIn]);

    // ── Orders listener ──
    useEffect(() => {
        if (!loggedIn) return;
        const q = query(collection(db, "orders"), where("shopId", "==", SHOP_ID));
        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setOrders(data);
            const pendingCount = data.filter((o) => o.status === "pending").length;
            if (isFirstLoadRef.current) {
                prevOrderCountRef.current = pendingCount;
                isFirstLoadRef.current = false;
            } else if (pendingCount > prevOrderCountRef.current && soundEnabled) {
                playNotificationSound();
                prevOrderCountRef.current = pendingCount;
            } else {
                prevOrderCountRef.current = pendingCount;
            }
        });
        return unsub;
    }, [loggedIn, soundEnabled]);

    const dayStartedToday = dayStartTime
        ? new Date(dayStartTime * 1000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) === getTodayIST()
        : false;

    const startDay = async () => {
        const activeStatuses = ["pending", "approved", "paid", "preparing", "ready"];
        const activeOrders = dayOrders.filter((o) => activeStatuses.includes(o.status));
        if (activeOrders.length > 0) {
            alert(`${t("alert_cannot_start")}${activeOrders.length} ${t("alert_active_orders")}`);
            return;
        }
        if (!window.confirm(t("alert_start_day"))) return;

        const sessionNumber = sessions.length + 1;
        if (sessions.length > 0) {
            const lastSession = sessions[0];
            await updateDoc(doc(db, "shopSessions", lastSession.id), { endTime: serverTimestamp() });
        }
        await setDoc(doc(db, "shopSessions", `${SHOP_ID}_${sessionNumber}`), {
            shopId: SHOP_ID, sessionNumber, startTime: serverTimestamp(), endTime: null,
        });
        await setDoc(doc(db, "shopSettings", SHOP_ID), {
            dayStartTime: serverTimestamp(), shopId: SHOP_ID,
        }, { merge: true });
        await setDoc(doc(db, "counters", SHOP_ID), { lastToken: 0, shopId: SHOP_ID });
        setDashSessionIdx(0);
    };

    const updateStatus = async (orderId, newStatus) => {
        await updateDoc(doc(db, "orders", orderId), { status: newStatus });
        setSelectedOrder((prev) => prev?.id === orderId ? { ...prev, status: newStatus } : prev);
    };

    const cancelOrder = async (orderId) => {
        if (!window.confirm(t("alert_cancel_order"))) return;
        await updateDoc(doc(db, "orders", orderId), { status: "cancelled" });
        setSelectedOrder((prev) => prev?.id === orderId ? { ...prev, status: "cancelled" } : prev);
    };

    const toggleStock = async (itemId, currentlyInStock) => {
        const ref = doc(db, "stockStatus", SHOP_ID);
        await setDoc(ref, { [itemId]: !currentlyInStock }, { merge: true });
    };

    const dayOrders = dayStartTime
        ? orders.filter((o) => (o.createdAt?.seconds || 0) >= dayStartTime)
        : [];

    const filteredOrders = filter === "all"
        ? dayOrders
        : dayOrders.filter((o) => o.status === filter);

    const counts = {};
    ["pending", "approved", "paid", "preparing", "ready", "complete"].forEach((s) => {
        counts[s] = dayOrders.filter((o) => o.status === s).length;
    });
    counts.cancelled = dayOrders.filter((o) => o.status === "cancelled").length;
    counts.all = dayOrders.length;
    const activeCount = counts.pending + counts.approved + counts.paid + counts.preparing + counts.ready;

    // ── Dashboard calculations ──
    const currentSession = sessions[dashSessionIdx] || null;
    const isCurrentSession = dashSessionIdx === 0;
    const dashStartTime = currentSession ? currentSession.startTime?.seconds : dayStartTime;
    const dashEndTime = currentSession ? currentSession.endTime?.seconds || null : null;

    const dashOrders = orders.filter((o) => {
        if (!o.createdAt?.seconds || !dashStartTime) return false;
        if (dashEndTime) return o.createdAt.seconds >= dashStartTime && o.createdAt.seconds < dashEndTime;
        return o.createdAt.seconds >= dashStartTime;
    });
    const dashRevenue = dashOrders.filter((o) => ["paid", "preparing", "ready", "complete"].includes(o.status))
        .reduce((s, o) => s + (o.totalAmount || 0), 0);
    const dashCompleted = dashOrders.filter((o) => o.status === "complete").length;
    const dashPending = dashOrders.filter((o) => o.status === "pending").length;
    const avgOrderValue = dashOrders.length > 0
        ? Math.round(dashOrders.reduce((s, o) => s + (o.totalAmount || 0), 0) / dashOrders.length) : 0;

    const itemCounts = {};
    dashOrders.forEach((o) => {
        o.items?.forEach((item) => {
            const name = item.nameGu || item.name;
            const category = item.category || "";
            const key = name + "||" + category;
            itemCounts[key] = (itemCounts[key] || 0) + item.qty;
        });
    });
    const allSortedItems = Object.entries(itemCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([key, qty]) => { const [name, category] = key.split("||"); return { name, category, qty }; });
    const popularItems = allSortedItems.slice(0, 5);

    // ── New order cart ──
    const newCartItems = Object.values(newCart);
    const newCartTotal = newCartItems.reduce((s, i) => s + i.qty * i.price, 0);

    const addToNewCart = (item, category) => {
        setNewCart((prev) => ({
            ...prev,
            [item.id]: { ...item, qty: (prev[item.id]?.qty || 0) + 1, category: category || prev[item.id]?.category || "" },
        }));
    };

    const removeFromNewCart = (itemId) => {
        setNewCart((prev) => {
            const updated = { ...prev };
            if (updated[itemId].qty <= 1) delete updated[itemId];
            else updated[itemId].qty -= 1;
            return updated;
        });
    };

    const placeNewOrder = async () => {
        if (!newCustomerName.trim()) return alert(t("alert_enter_name"));
        if (newCartItems.length === 0) return alert(t("alert_add_items"));
        setPlacing(true);
        try {
            await placeOrderWithToken(SHOP_ID, {
                shopId: SHOP_ID,
                customerName: newCustomerName.trim(),
                items: newCartItems.map((i) => ({
                    id: i.id, name: i.nameEn, nameGu: i.name,
                    price: i.price, qty: i.qty, category: i.category || "",
                })),
                totalAmount: newCartTotal,
                createdBy: "admin",
            });
            setNewCart({});
            setNewCustomerName("");
            setView("orders");
        } catch (e) { alert("Error: " + e.message); }
        setPlacing(false);
    };

    // ════════════════════════════
    //  LOGIN
    // ════════════════════════════
    if (!loggedIn) {
        return (
            <div className="a-login">
                <div className="a-login-card">
                    <div className="a-login-logo">🍧</div>
                    <h2>{t("login_title")}</h2>
                    <input
                        type="password" placeholder={t("login_placeholder")} value={pass}
                        onChange={(e) => setPass(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && pass === ADMIN_PASS && setLoggedIn(true)}
                        className="a-login-input"
                    />
                    <button className="a-login-btn" onClick={() => {
                        if (pass === ADMIN_PASS) setLoggedIn(true);
                        else alert(t("login_wrong"));
                    }}>{t("login_btn")}</button>
                </div>
            </div>
        );
    }

    // ════════════════════════════
    //  SETTINGS
    // ════════════════════════════
    if (view === "settings") {
        return (
            <div className="a-app">
                <header className="a-header">
                    <button className="a-back" onClick={() => setView("orders")}>{t("back")}</button>
                    <span>{t("settings_title")}</span>
                    <span />
                </header>
                <div className="a-settings">
                    {/* Stock Management */}
                    <div className="a-setting-section">
                        <div className="a-setting-section-title">{t("settings_stock_section")}</div>
                        <div className="a-toggle-row">
                            <div className="a-toggle-info">
                                <div className="a-toggle-label">{t("settings_stock_label")}</div>
                                <div className="a-toggle-desc">{t("settings_stock_desc")}</div>
                            </div>
                            <label className="a-toggle">
                                <input
                                    type="checkbox"
                                    checked={stockToggleEnabled}
                                    onChange={(e) => handleSetStockToggle(e.target.checked)}
                                />
                                <span className="a-toggle-slider" />
                            </label>
                        </div>
                    </div>

                    {/* Language */}
                    <div className="a-setting-section">
                        <div className="a-setting-section-title">{t("settings_lang_section")}</div>
                        <div className="a-lang-pills">
                            <button
                                className={`a-lang-pill ${language === "en" ? "active" : ""}`}
                                onClick={() => handleSetLanguage("en")}
                            >
                                🇬🇧 {t("settings_lang_en")}
                            </button>
                            <button
                                className={`a-lang-pill ${language === "gu" ? "active" : ""}`}
                                onClick={() => handleSetLanguage("gu")}
                            >
                                🇮🇳 {t("settings_lang_gu")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ════════════════════════════
    //  DASHBOARD
    // ════════════════════════════
    if (view === "dashboard") {
        return (
            <div className="a-app">
                <header className="a-header">
                    <button className="a-back" onClick={() => setView("orders")}>{t("back")}</button>
                    <span>{t("dashboard")}</span>
                    <span />
                </header>
                <div className="a-dashboard">
                    {/* Session picker */}
                    <div className="a-dash-date-row">
                        <button
                            onClick={() => setDashSessionIdx(i => i + 1)}
                            disabled={dashSessionIdx >= sessions.length - 1}>‹</button>
                        <div className="a-dash-date">
                            {dashStartTime ? (
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                                        {formatTime(dashStartTime)} — {new Date(dashStartTime * 1000).toLocaleDateString("en-IN", {
                                            weekday: "short", day: "numeric", month: "long", year: "numeric",
                                            timeZone: "Asia/Kolkata"
                                        })}
                                    </div>
                                    {dashEndTime && (
                                        <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
                                            to {formatTime(dashEndTime)} — {new Date(dashEndTime * 1000).toLocaleDateString("en-IN", {
                                                weekday: "short", day: "numeric", month: "short",
                                                timeZone: "Asia/Kolkata"
                                            })}
                                        </div>
                                    )}
                                    {isCurrentSession && <span className="a-today-badge" style={{ marginTop: 4, display: "inline-block" }}>{t("current")}</span>}
                                </div>
                            ) : (
                                <span style={{ fontSize: 13, color: "#aaa" }}>{t("no_sessions")}</span>
                            )}
                        </div>
                        <button
                            onClick={() => setDashSessionIdx(i => i - 1)}
                            disabled={dashSessionIdx === 0}>›</button>
                    </div>

                    {/* Revenue hero */}
                    <div className="a-dash-revenue">
                        <div className="a-dash-revenue-label">{t("revenue")}</div>
                        <div className="a-dash-revenue-amount">₹{dashRevenue.toLocaleString()}</div>
                        <div className="a-dash-revenue-sub">{dashCompleted} {t("orders_completed")}</div>
                    </div>

                    {/* Stats */}
                    <div className="a-dash-grid">
                        <div className="a-dash-stat a-dash-stat-btn" style={{ background: "#fff3ee" }}
                            onClick={() => setDashView(dashView === "all" ? null : "all")}>
                            <div className="a-dash-stat-num" style={{ color: "#ff6b35" }}>{dashOrders.length}</div>
                            <div className="a-dash-stat-label">{t("total_orders")} {dashView === "all" ? "▲" : "▼"}</div>
                        </div>
                        <div className="a-dash-stat a-dash-stat-btn" style={{ background: "#e8f5e9" }}
                            onClick={() => setDashView(dashView === "completed" ? null : "completed")}>
                            <div className="a-dash-stat-num" style={{ color: "#4caf50" }}>{dashCompleted}</div>
                            <div className="a-dash-stat-label">{t("completed")} {dashView === "completed" ? "▲" : "▼"}</div>
                        </div>
                        <div className="a-dash-stat" style={{ background: "#e3f2fd" }}>
                            <div className="a-dash-stat-num" style={{ color: "#2196f3" }}>₹{avgOrderValue}</div>
                            <div className="a-dash-stat-label">{t("avg_order")}</div>
                        </div>
                        <div className="a-dash-stat a-dash-stat-btn" style={{ background: "#fff8e1" }}
                            onClick={() => setDashView(dashView === "pending" ? null : "pending")}>
                            <div className="a-dash-stat-num" style={{ color: "#ff9800" }}>{dashPending}</div>
                            <div className="a-dash-stat-label">{t("pending")} {dashView === "pending" ? "▲" : "▼"}</div>
                        </div>
                    </div>

                    {/* Drill-down order list */}
                    {dashView && (() => {
                        const viewOrders = dashView === "all" ? dashOrders
                            : dashView === "completed" ? dashOrders.filter(o => o.status === "complete")
                                : dashOrders.filter(o => o.status === "pending");
                        const viewLabel = dashView === "all" ? t("all_orders")
                            : dashView === "completed" ? t("completed_orders")
                                : t("pending_orders");
                        return (
                            <div className="a-dash-orders">
                                <div className="a-dash-orders-title">{viewLabel} ({viewOrders.length})</div>
                                {viewOrders.length === 0 ? (
                                    <div className="a-empty" style={{ padding: "20px 0" }}>{t("no_orders")}</div>
                                ) : viewOrders.map((order) => {
                                    const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                                    const isExp = expandedOrderId === order.id;
                                    return (
                                        <div key={order.id} className="a-dash-order-row"
                                            style={{ borderLeft: `3px solid ${cfg.color}`, cursor: "pointer" }}
                                            onClick={() => setExpandedOrderId(isExp ? null : order.id)}>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ background: cfg.color, color: "white", borderRadius: 6, padding: "2px 7px", fontSize: 12, fontWeight: 800 }}>
                                                        #{order.tokenNumber}
                                                    </span>
                                                    <span style={{ fontWeight: 700, fontSize: 14 }}>{order.customerName}</span>
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ fontWeight: 800, color: cfg.color, fontSize: 13 }}>₹{order.totalAmount}</span>
                                                    <span style={{ fontSize: 12, color: "#bbb" }}>{isExp ? "▲" : "▼"}</span>
                                                </div>
                                            </div>
                                            <div style={{ fontSize: 12, color: "#999", marginTop: 3 }}>{formatTime(order.createdAt?.seconds)}</div>
                                            {isExp && (
                                                <div style={{ marginTop: 10, borderTop: "1px solid #eee", paddingTop: 8 }}>
                                                    {order.items?.map((item, i) => (
                                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid #f5f5f5" }}>
                                                            <span style={{ fontWeight: 800, color: cfg.color, fontSize: 13, minWidth: 24 }}>{item.qty}x</span>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{item.nameGu || item.name}</div>
                                                                {item.category && <div style={{ fontSize: 11, color: "#555" }}>{item.category}</div>}
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: 13 }}>₹{item.price * item.qty}</span>
                                                        </div>
                                                    ))}
                                                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 14, paddingTop: 8, borderTop: "2px solid #eee", color: "var(--brand)", marginTop: 4 }}>
                                                        <span>{t("total")}</span>
                                                        <span>₹{order.totalAmount}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}

                    {/* Popular items */}
                    {allSortedItems.length > 0 && (
                        <div className="a-dash-popular">
                            <div className="a-dash-section-title">{t("popular_items")}</div>
                            {popularItems.map(({ name, category, qty }, i) => (
                                <div key={name + category} className="a-dash-popular-item">
                                    <div className="a-dash-rank" style={{
                                        background: i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : i === 2 ? "#cd7f32" : "#eee",
                                        color: i < 3 ? "white" : "#666"
                                    }}>{i + 1}</div>
                                    <div>
                                        <div className="a-dash-popular-name">{name}</div>
                                        {category && <div className="a-dash-popular-cat">{category}</div>}
                                    </div>
                                    <div className="a-dash-popular-qty">{qty} {t("sold")}</div>
                                    <div className="a-dash-bar-wrap">
                                        <div className="a-dash-bar" style={{
                                            width: `${Math.round((qty / (allSortedItems[0].qty || 1)) * 100)}%`,
                                            background: i === 0 ? "#ff6b35" : "#ddd"
                                        }} />
                                    </div>
                                </div>
                            ))}

                            {showAllItems && allSortedItems.slice(5).length > 0 && (() => {
                                const categories = [...new Set(allSortedItems.slice(5).map(i => i.category || "Other"))];
                                return categories.map(cat => (
                                    <div key={cat}>
                                        <div className="a-dash-cat-header">{cat}</div>
                                        {allSortedItems.slice(5).filter(i => (i.category || "Other") === cat).map(({ name, category, qty }) => (
                                            <div key={name + category} className="a-dash-popular-item">
                                                <div className="a-dash-rank" style={{ background: "#eee", color: "#666" }}>—</div>
                                                <div>
                                                    <div className="a-dash-popular-name">{name}</div>
                                                    {category && <div className="a-dash-popular-cat">{category}</div>}
                                                </div>
                                                <div className="a-dash-popular-qty">{qty} {t("sold")}</div>
                                                <div className="a-dash-bar-wrap">
                                                    <div className="a-dash-bar" style={{
                                                        width: `${Math.round((qty / (allSortedItems[0].qty || 1)) * 100)}%`,
                                                        background: "#ddd"
                                                    }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ));
                            })()}

                            {allSortedItems.length > 5 && (
                                <button className="a-show-all-btn" onClick={() => setShowAllItems(v => !v)}>
                                    {showAllItems ? t("show_less") : `${t("show_all")} ${allSortedItems.length} ${t("items")}`}
                                </button>
                            )}
                        </div>
                    )}

                    {dashOrders.length === 0 && (
                        <div className="a-empty" style={{ marginTop: 40 }}>
                            {isCurrentSession && !dayStartTime ? t("day_not_started_dash") : t("no_orders_session")}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ════════════════════════════
    //  ORDER DETAIL
    // ════════════════════════════
    if (selectedOrder) {
        const cfg = STATUS_CONFIG[selectedOrder.status] || STATUS_CONFIG.pending;
        return (
            <div className="a-app">
                <header className="a-header">
                    <button className="a-back" onClick={() => setSelectedOrder(null)}>{t("back")}</button>
                    <span>{t("order_details")}</span>
                    <span />
                </header>
                <div className="a-detail">
                    <div className="a-detail-top" style={{ background: cfg.bgLight }}>
                        <div className="a-token-number">#{selectedOrder.tokenNumber || "—"}</div>
                        <div className="a-detail-name">{selectedOrder.customerName}</div>
                        {selectedOrder.orderId && <div className="a-detail-orderid">{selectedOrder.orderId}</div>}
                        {selectedOrder.createdAt && (
                            <div className="a-detail-time">🕐 {formatDateTime(selectedOrder.createdAt.seconds)}</div>
                        )}
                        <div className="a-status-badge" style={{ background: cfg.color }}>{cfg.label}</div>
                        {selectedOrder.createdBy === "admin" && <div className="a-badge-manual">{t("manual_order")}</div>}
                    </div>
                    <div className="a-detail-items">
                        {selectedOrder.items?.map((item, i) => (
                            <div key={i} className="a-detail-item">
                                <span className="a-item-qty">{item.qty}x</span>
                                <div style={{ flex: 1 }}>
                                    <div className="a-item-name">{item.nameGu || item.name}</div>
                                    {item.category && <div className="a-item-category">{item.category}</div>}
                                </div>
                                <span className="a-item-price">₹{item.price * item.qty}</span>
                            </div>
                        ))}
                    </div>
                    <div className="a-detail-total">
                        <span>{t("total")}</span>
                        <span>₹{selectedOrder.totalAmount}</span>
                    </div>
                    <div className="a-flow-steps">
                        {["pending", "approved", "paid", "preparing", "ready", "complete"].map((s) => {
                            const stepCfg = STATUS_CONFIG[s];
                            const statuses = ["pending", "approved", "paid", "preparing", "ready", "complete"];
                            const isDone = statuses.indexOf(s) <= statuses.indexOf(selectedOrder.status);
                            return (
                                <div key={s} className={`a-flow-step ${isDone ? "done" : ""}`}>
                                    <div className="a-flow-dot" style={{ background: isDone ? stepCfg.color : "#ddd" }} />
                                    <div className="a-flow-label" style={{ color: isDone ? stepCfg.color : "#bbb" }}>{stepCfg.label}</div>
                                </div>
                            );
                        })}
                    </div>
                    {cfg.next && (
                        <button className="a-action-btn" style={{ background: STATUS_CONFIG[cfg.next]?.color || cfg.color }}
                            onClick={() => updateStatus(selectedOrder.id, cfg.next)}>
                            {cfg.nextLabel}
                        </button>
                    )}
                    {["pending", "approved"].includes(selectedOrder.status) && (
                        <button className="a-action-btn" style={{ background: "#f44336", marginTop: 8 }}
                            onClick={() => cancelOrder(selectedOrder.id)}>
                            {t("cancel_order")}
                        </button>
                    )}
                    {selectedOrder.status === "complete" && <div className="a-paid-label">{t("order_complete")}</div>}
                    {selectedOrder.status === "cancelled" && <div className="a-paid-label" style={{ background: "#ffebee", color: "#f44336" }}>{t("order_cancelled")}</div>}
                </div>
            </div>
        );
    }

    // ════════════════════════════
    //  NEW MANUAL ORDER
    // ════════════════════════════
    if (view === "neworder") {
        return (
            <div className="a-app">
                <header className="a-header">
                    <button className="a-back" onClick={() => setView("orders")}>{t("back")}</button>
                    <span>{t("new_manual_order")}</span>
                    <span />
                </header>
                <div className="a-neworder">
                    <input className="a-name-input" placeholder={t("customer_name_placeholder")} value={newCustomerName}
                        onChange={(e) => setNewCustomerName(e.target.value)} />
                    <div className="c-tabs" style={{ margin: "12px 0" }}>
                        {Object.keys(MENU).map((cat) => (
                            <button key={cat} className={`c-tab ${newCategory === cat ? "active" : ""}`}
                                onClick={() => setNewCategory(cat)}>
                                {CATEGORY_ICONS[cat]} {cat}
                            </button>
                        ))}
                    </div>
                    <div className="c-menu-list">
                        {MENU[newCategory].map((item) => {
                            const inCart = newCart[item.id];
                            const inStock = stockStatus[item.id] !== false;
                            const isPopular = popularItemIds.has(item.id);
                            return (
                                <div key={item.id} className={`c-menu-item${isPopular && inStock ? " c-menu-item-popular" : ""}${!inStock ? " c-menu-item-oos" : ""}`}
                                    style={{ opacity: inStock ? 1 : 0.6 }}>
                                    <div className="c-menu-item-info">
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                            <div className="c-menu-item-name" style={{ color: inStock ? "var(--text)" : "#bbb" }}>{item.name}</div>
                                            {isPopular && inStock && <span className="c-popular-badge">🔥 Popular</span>}
                                        </div>
                                        <div className="c-menu-item-subname">{item.nameEn}</div>
                                        <div className="c-menu-item-price">₹{item.price}</div>
                                    </div>
                                    <div className="c-menu-item-action" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                        {/* Stock toggle — only shown when stockToggleEnabled is ON */}
                                        {stockToggleEnabled && (
                                            <button
                                                onClick={() => toggleStock(item.id, inStock)}
                                                style={{
                                                    border: "none", borderRadius: 20, padding: "3px 10px",
                                                    fontSize: 10, fontWeight: 700, cursor: "pointer",
                                                    fontFamily: "inherit",
                                                    background: inStock ? "#e8f5e9" : "#ffebee",
                                                    color: inStock ? "#4caf50" : "#f44336",
                                                }}>
                                                {inStock ? t("in_stock") : t("out_of_stock")}
                                            </button>
                                        )}
                                        {/* Add/qty controls — only if in stock */}
                                        {inStock ? (inCart ? (
                                            <div className="c-qty-controls">
                                                <button onClick={() => removeFromNewCart(item.id)}>−</button>
                                                <span>{inCart.qty}</span>
                                                <button onClick={() => addToNewCart(item, newCategory)}>+</button>
                                            </div>
                                        ) : (
                                            <button className="c-add-btn" onClick={() => addToNewCart(item, newCategory)}>+ Add</button>
                                        )) : (
                                            <span style={{ fontSize: 11, color: "#f44336", fontWeight: 700 }}>{t("sold_out")}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {newCartItems.length > 0 && (
                        <div className="a-cart-summary">
                            <div className="a-cart-summary-title">{t("cart")}</div>
                            {newCartItems.map((item) => (
                                <div key={item.id} className="a-cart-summary-item">
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{item.name}</div>
                                        {item.category && <div style={{ fontSize: 11, color: "#555" }}>{item.category}</div>}
                                    </div>
                                    <div className="c-qty-controls" style={{ transform: "scale(0.85)" }}>
                                        <button onClick={() => removeFromNewCart(item.id)}>−</button>
                                        <span>{item.qty}</span>
                                        <button onClick={() => addToNewCart(item, item.category)}>+</button>
                                    </div>
                                    <div style={{ fontWeight: 700, minWidth: 48, textAlign: "right" }}>₹{item.price * item.qty}</div>
                                </div>
                            ))}
                            <div className="a-cart-summary-total">
                                <span>{t("total")}</span>
                                <span>₹{newCartTotal}</span>
                            </div>
                        </div>
                    )}
                    {newCartItems.length > 0 && (
                        <div className="c-sticky-bar" onClick={placing ? undefined : placeNewOrder}>
                            <span>{newCartItems.reduce((s, i) => s + i.qty, 0)} {t("items_added")}</span>
                            <span>{placing ? t("placing") : `${t("place_order")} • ₹${newCartTotal}`}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ════════════════════════════
    //  ORDERS LIST
    // ════════════════════════════
    return (
        <div className="a-app">
            <header className="a-header">
                <div className="a-header-title">
                    🍧 {SHOP_NAME}
                    {activeCount > 0 && <span className="a-notif-dot">{activeCount}</span>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button className="a-icon-btn" onClick={() => setSoundEnabled((v) => !v)}>
                        {soundEnabled ? "🔔" : "🔕"}
                    </button>
                    <button className="a-icon-btn" onClick={() => setView("dashboard")}>📊</button>
                    <button className="a-icon-btn" onClick={() => setView("settings")}>⚙️</button>
                    <button className="a-new-order-btn" onClick={() => setView("neworder")}>+ New</button>
                </div>
            </header>

            {/* Day start strip */}
            <div className="a-day-strip">
                <div className="a-day-strip-info">
                    {dayStartedToday
                        ? <span>🟢 {t("day_started_at")} <strong>{formatTime(dayStartTime)}</strong></span>
                        : <span>⚪ {t("day_not_started")}</span>
                    }
                </div>
                <button className="a-start-day-btn" onClick={startDay}>
                    {dayStartedToday ? t("restart_day") : t("start_day")}
                </button>
            </div>

            {/* Stats Row 1: All, New, Approved, Paid */}
            <div className="a-stats" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                <div className={`a-stat ${filter === "all" ? "a-stat-active" : ""}`}
                    style={{ background: "#f0f0f0" }} onClick={() => setFilter("all")}>
                    <span className="a-stat-num" style={{ color: "#444" }}>{counts.all}</span>
                    <span className="a-stat-label">{t("stat_all")}</span>
                </div>
                {["pending", "approved", "paid"].map((s) => {
                    const c = STATUS_CONFIG[s];
                    return (
                        <div key={s} className={`a-stat ${filter === s ? "a-stat-active" : ""}`}
                            style={{ background: c.bgLight }} onClick={() => setFilter(s)}>
                            <span className="a-stat-num" style={{ color: c.color }}>{counts[s]}</span>
                            <span className="a-stat-label">{c.label}</span>
                        </div>
                    );
                })}
            </div>
            {/* Stats Row 2: Preparing, Ready, Complete, Cancel */}
            <div className="a-stats" style={{ gridTemplateColumns: "repeat(4,1fr)", marginTop: 0, paddingTop: 0 }}>
                {["preparing", "ready", "complete"].map((s) => {
                    const c = STATUS_CONFIG[s];
                    return (
                        <div key={s} className={`a-stat ${filter === s ? "a-stat-active" : ""}`}
                            style={{ background: c.bgLight }} onClick={() => setFilter(s)}>
                            <span className="a-stat-num" style={{ color: c.color }}>{counts[s]}</span>
                            <span className="a-stat-label">{c.label}</span>
                        </div>
                    );
                })}
                <div className={`a-stat ${filter === "cancelled" ? "a-stat-active" : ""}`}
                    style={{ background: "#ffebee" }} onClick={() => setFilter("cancelled")}>
                    <span className="a-stat-num" style={{ color: "#f44336" }}>{counts.cancelled}</span>
                    <span className="a-stat-label">{t("stat_cancel")}</span>
                </div>
            </div>

            {/* Orders list */}
            <div className="a-orders-list">
                {!dayStartTime ? (
                    <div className="a-day-not-started">
                        <div style={{ fontSize: 48 }}>☀️</div>
                        <div className="a-day-not-started-title">{t("day_not_started_title")}</div>
                        <div className="a-day-not-started-sub">{t("day_not_started_sub")}</div>
                        <button className="a-start-day-btn" style={{ marginTop: 16, padding: "10px 24px", fontSize: 14 }} onClick={startDay}>
                            {t("start_day")}
                        </button>
                    </div>
                ) : filteredOrders.length === 0 ? (
                    <div className="a-empty">{t("no_orders")}</div>
                ) : (
                    filteredOrders.map((order) => {
                        const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                        const isExpanded = expandedOrderId === order.id;
                        return (
                            <div key={order.id} className="a-order-card"
                                style={{ borderLeft: `4px solid ${cfg.color}` }}>

                                {/* ── Main row ── */}
                                <div className="a-card-main"
                                    onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}>

                                    {/* Token */}
                                    <div className="a-card-token" style={{ background: cfg.color }}>
                                        #{order.tokenNumber || "—"}
                                    </div>

                                    {/* Center */}
                                    <div className="a-card-center">
                                        <div className="a-card-name">{order.customerName}</div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                                            <span className="a-card-amount">₹{order.totalAmount}</span>
                                            <span style={{ background: cfg.color, color: "white", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{cfg.label}</span>
                                        </div>
                                        <div className="a-card-time">{formatTime(order.createdAt?.seconds)}</div>
                                    </div>

                                    {/* Right: buttons */}
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", flexShrink: 0, alignSelf: "stretch", paddingTop: 2, paddingBottom: 2 }}>
                                        <button
                                            style={{ background: "#f0f0f0", border: "none", borderRadius: 8, width: 28, height: 28, fontSize: 20, color: "#999", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                                            onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); }}>
                                            ›
                                        </button>
                                        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4 }}>
                                            {["pending", "approved"].includes(order.status) && (
                                                <button
                                                    style={{ border: "2px solid #f44336", color: "#f44336", background: "white", borderRadius: 20, padding: "4px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                                                    onClick={(e) => { e.stopPropagation(); cancelOrder(order.id); }}>
                                                    ✕
                                                </button>
                                            )}
                                            {cfg.next && (
                                                <button
                                                    style={{ border: `2px solid ${cfg.color}`, color: cfg.color, background: "white", borderRadius: 20, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                                                    onClick={(e) => { e.stopPropagation(); updateStatus(order.id, cfg.next); }}>
                                                    {cfg.nextLabel}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Expanded items ── */}
                                {isExpanded && (
                                    <div className="a-order-expanded">
                                        {order.items?.map((item, i) => (
                                            <div key={i} className="a-expanded-item">
                                                <span className="a-item-qty">{item.qty}x</span>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>{item.nameGu || item.name}</div>
                                                    {item.category && <div style={{ fontSize: 11, color: "#555" }}>{item.category}</div>}
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: 13, minWidth: 48, textAlign: "right" }}>₹{item.price * item.qty}</span>
                                            </div>
                                        ))}
                                        <div className="a-expanded-total">
                                            <span>{t("total")}</span>
                                            <span>₹{order.totalAmount}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}